import { ArrowRight, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/lib/utils';

export interface Shortcut {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** `attention` para o que cobra ação de quem olha; `neutral` para o resto. */
  tone: 'attention' | 'neutral';
}

interface HomeShortcutsProps {
  shortcuts: readonly Shortcut[];
  className?: string;
}

/**
 * A primeira linha da Início: o que depende de você, com o número junto.
 *
 * ⚠️ **Cada atalho leva à tela que PRODUZ aquele número, e nenhum deles é uma
 * soma.** Rascunho e projeto devolvido esperam a mesma pessoa, e a tentação de
 * fundi-los num "3 pendências" é forte — mas as duas populações moram em telas
 * diferentes (`/rascunhos` e a caixa de entrada do Calendário), e um número que
 * não bate com nenhuma delas é a origem exata do bug de confiança que este
 * painel não pode ter: a pessoa clica, conta o que vê e desconta a diferença do
 * sistema inteiro.
 *
 * ⚠️ **A Início NÃO reimplementa a caixa de entrada** (decisão 1 do plano). Ela
 * mora no Calendário, que a [ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)
 * §6 estabeleceu como a oficina de quem executa; aqui há um atalho com número, e
 * é só. Duas caixas de entrada seriam a terceira verdade sobre o mesmo assunto.
 *
 * **Atalho com zero não é renderizado** — a página filtra antes. Uma fileira
 * permanentemente escrita "0 rascunhos" ensina o olho a pular a região onde a
 * novidade vai aparecer, e no dia em que houver novidade ele pula de novo. É a
 * mesma regra do `StatusTiles` e da caixa de entrada.
 */
export function HomeShortcuts({ shortcuts, className }: HomeShortcutsProps) {
  if (shortcuts.length === 0) return null;

  return (
    <div className={cn('stagger flex flex-wrap gap-2', className)}>
      {shortcuts.map((shortcut) => (
        <Link
          key={shortcut.key}
          to={shortcut.to}
          className={cn(
            'group flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium',
            'transition-[background-color,border-color,transform] duration-fast ease-out active:scale-[0.99]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            shortcut.tone === 'attention'
              ? 'border-amber-300 bg-amber-500/10 text-amber-900 hover:bg-amber-500/20'
              : 'border-border bg-card hover:bg-accent',
          )}
        >
          <shortcut.icon aria-hidden="true" className="size-4 shrink-0" />
          <span>{shortcut.label}</span>
          <ArrowRight
            aria-hidden="true"
            className="size-4 shrink-0 opacity-60 transition-transform duration-fast ease-out group-hover:translate-x-0.5"
          />
        </Link>
      ))}
    </div>
  );
}
